import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { NpsQuestion } from '@/shared/types/nps';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { contactInputProps, maskPhone } from './partition-questions';
import type { AnswerValue } from './public-survey-question-field';

interface PublicSurveyUserInfoStepProps {
  questions: NpsQuestion[];
  answers: Record<string, AnswerValue>;
  onChange: (questionId: string, value: AnswerValue) => void;
}

export function PublicSurveyUserInfoStep({ questions, answers, onChange }: PublicSurveyUserInfoStepProps) {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusNext = (index: number) => {
    const next = inputRefs.current[index + 1];
    if (next) {
      next.focus();
      return true;
    }
    return false;
  };

  return (
    <div className="glass space-y-4 rounded-2xl p-4 sm:space-y-5 sm:p-6">
      <div className="space-y-1 text-center">
        <h2 className="text-lg font-semibold tracking-tight">{t('wizard.userData')}</h2>
        <p className="text-sm text-muted-foreground">{t('wizard.userDataHint')}</p>
      </div>
      <div className="space-y-4">
        {questions.map((q, index) => {
          const props = contactInputProps(q.title, t);
          const isLast = index === questions.length - 1;
          return (
            <div key={q.id} className="space-y-2">
              <Label htmlFor={`user-info-${q.id}`} className="text-sm font-medium">
                {q.title}
                {(q.required ?? true) ? <span className="ml-0.5 text-destructive">*</span> : null}
              </Label>
              {q.description ? (
                <p className="text-xs text-muted-foreground">{q.description}</p>
              ) : null}
              <Input
                id={`user-info-${q.id}`}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                value={(answers[q.id] as string) ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  onChange(q.id, props.kind === 'phone' ? maskPhone(raw) : raw);
                }}
                placeholder={props.placeholder}
                className="h-12 text-base"
                autoComplete={props.autoComplete}
                type={props.type}
                inputMode={props.inputMode}
                enterKeyHint={isLast ? 'go' : 'next'}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  if (!focusNext(index)) {
                    (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
